import { hexToRgb, luminance, mixRgb, smoothstep } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// The two colourways from the design: violet on slate, then orange on sand.
const COOL = {
  skyA: hexToRgb('#737B99'),
  skyB: hexToRgb('#899BA3'),
  light: hexToRgb('#6E5FE6'),
  dark: hexToRgb('#5344CB'),
  ribbon: hexToRgb('#6A09DD'),
  glow: hexToRgb('#B9AEFF'),
};
// Midway the world passes through a dusky lilac instead of a flat grey.
const DUSK = {
  skyA: hexToRgb('#9C8FB2'),
  skyB: hexToRgb('#B6A9BC'),
  light: hexToRgb('#B44FC9'),
  dark: hexToRgb('#8433A6'),
  ribbon: hexToRgb('#B21FB8'),
  glow: hexToRgb('#E9B4F2'),
};
const WARM = {
  skyA: hexToRgb('#E6D9CC'),
  skyB: hexToRgb('#DBCFD9'),
  light: hexToRgb('#FD4319'),
  dark: hexToRgb('#B43012'),
  ribbon: hexToRgb('#FD4319'),
  glow: hexToRgb('#FFC2AE'),
};

export type PyramidPalette = typeof COOL;

/** The pyramid world warms from violet-on-slate to orange-on-sand as the learner progresses. */
export const pyramidPalette = (progress: number): PyramidPalette => {
  const blend = smoothstep(0.04, 0.96, progress);
  const [from, to, amount] = blend < 0.5 ? [COOL, DUSK, blend * 2] : [DUSK, WARM, (blend - 0.5) * 2];
  return {
    skyA: mixRgb(from.skyA, to.skyA, amount),
    skyB: mixRgb(from.skyB, to.skyB, amount),
    light: mixRgb(from.light, to.light, amount),
    dark: mixRgb(from.dark, to.dark, amount),
    ribbon: mixRgb(from.ribbon, to.ribbon, amount),
    glow: mixRgb(from.glow, to.glow, amount),
  };
};

/**
 * White header text on the slate start (as in the design), dark text once the
 * sky has lightened enough that dark text reads better. The header spans the
 * whole width, so the cut-over uses the average of both gradient ends.
 */
export const pyramidChromeTone = (progress: number): ChromeTone => {
  const { skyA, skyB } = pyramidPalette(progress);
  return (luminance(skyA) + luminance(skyB)) / 2 > 0.31 ? 'dark' : 'light';
};
